import React from "react";
import styled, { keyframes } from "styled-components";
import {
  FiArrowDown,
  FiArrowUpRight,
  FiCode,
  FiGithub,
  FiLinkedin,
} from "react-icons/fi";
import { FaStackOverflow } from "react-icons/fa";
import profile from "../../assets/Me/MaximilianGaber.jpeg";

const socialLinks = [
  { label: "GitHub", href: "https://github.com/ukalto", Icon: FiGithub },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/maximilian-gaber/",
    Icon: FiLinkedin,
  },
  {
    label: "Stack Overflow",
    href: "https://stackoverflow.com/users/12347865/max",
    Icon: FaStackOverflow,
  },
];

const Home = () => (
  <Container id="home" aria-labelledby="home-title">
    <Content>
      <Intro>
        <Eyebrow>
          <span aria-hidden="true" /> Full-Stack Developer
        </Eyebrow>
        <Greeting>Hey, I’m</Greeting>
        <h1 id="home-title">
          Maximilian
          <br />
          <span>Gaber.</span>
        </h1>
        <Description>
          Turning ideas into thoughtful digital experiences. I build for the
          web, from the first interface to the logic behind it.
        </Description>
        <Actions>
          <ProjectLink href="#projects">
            View my projects <FiArrowUpRight aria-hidden="true" />
          </ProjectLink>
          <AboutLink href="#aboutme">
            More about me <FiArrowDown aria-hidden="true" />
          </AboutLink>
        </Actions>
        <Social aria-label="Social profiles">
          {socialLinks.map(({ label, href, Icon }) => (
            <a key={label} href={href} aria-label={label} title={label}>
              <Icon aria-hidden="true" />
            </a>
          ))}
          <span>Code, ideas & everything in between.</span>
        </Social>
      </Intro>
      <Portrait>
        <PhotoFrame>
          <img
            src={profile}
            alt="Maximilian Gaber"
            width="768"
            height="1024"
            decoding="async"
          />
          <CodeBadge aria-hidden="true">
            <FiCode />
          </CodeBadge>
        </PhotoFrame>
        <figcaption>
          <span className="caption-line" aria-hidden="true" />
          <span>
            Curiosity led me here.
            <br />
            <strong>Building since 2015.</strong>
          </span>
        </figcaption>
      </Portrait>
    </Content>
    <Footnote>
      <span>From frontend details to backend systems.</span>
      <a href="#aboutme">
        Explore the portfolio <FiArrowDown aria-hidden="true" />
      </a>
    </Footnote>
  </Container>
);

export default Home;

const enter = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.section`
  position: relative;
  isolation: isolate;
  padding: 9rem max(5%, calc((100% - 1120px) / 2)) 3rem;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 86% 35%, #eaf9f3 0%, transparent 52%), #fcfdfc;

  a:focus-visible {
    outline: 2px solid #007b61;
    outline-offset: 5px;
  }

  @media (min-width: 840px) {
    padding-top: clamp(10rem, 17vh, 13rem);
    padding-bottom: 2.5rem;
  }

  @media (max-width: 839px) {
    padding: 7.5rem 1.25rem 2rem;
    background:
      radial-gradient(ellipse at 50% 65%, #eaf9f3 0%, transparent 65%), #fcfdfc;
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation: none;
      transition: none;
    }
  }
`;

const Content = styled.div`
  display: grid;
  align-items: center;
  gap: 3.5rem;

  @media (max-width: 839px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
    max-width: 34rem;
    margin-inline: auto;
    text-align: center;
  }

  @media (min-width: 840px) {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    gap: 4rem;
  }
`;

const Intro = styled.div`
  animation: ${enter} 650ms ease both;

  h1 {
    margin-top: 0.5rem;
    font-size: clamp(2.6rem, 6.3vw, 5.75rem);
    font-weight: 600;
    line-height: 1.06;
    letter-spacing: -0.065em;

    span {
      color: #008369;
    }
  }

  @media (max-width: 839px) {
    h1 {
      font-size: clamp(2.65rem, 10.5vw, 4.5rem);
      line-height: 1.05;
    }
  }
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 2rem;
  color: #476259;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  @media (max-width: 839px) {
    justify-content: center;
    margin-bottom: 1.25rem;
    font-size: 0.65rem;
    letter-spacing: 0.11em;
  }

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #008369;
    box-shadow: 0 0 0 5px rgba(1, 190, 150, 0.09);
  }
`;

const Greeting = styled.p`
  font-size: 1.2rem;
  color: #59615e;
`;

const Description = styled.p`
  max-width: 29rem;
  margin-top: 1.5rem;
  font-size: clamp(0.95rem, 1.3vw, 1.05rem);
  line-height: 1.85;
  color: #59615e;

  @media (max-width: 839px) {
    max-width: 30ch;
    margin: 1.1rem auto 0;
    font-size: 0.9rem;
    line-height: 1.75;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.5rem;
  margin-top: 2rem;

  @media (max-width: 839px) {
    justify-content: center;
    gap: 0.5rem 1.25rem;
    margin-top: 1.5rem;
  }

  @media (max-width: 420px) {
    flex-direction: column;

    a {
      width: 100%;
      max-width: 18rem;
    }
  }

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    min-height: 48px;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const ProjectLink = styled.a`
  padding: 0.9rem 1.35rem;
  border-radius: 999px;
  background: #183c32;
  color: #fff;
  box-shadow: 0 6px 16px rgba(24, 60, 50, 0.1);
  transition:
    transform 180ms ease,
    background-color 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: #006e57;
  }
`;

const AboutLink = styled.a`
  color: #354b42;
  border-radius: 4px;

  &:hover {
    color: #007b61;
  }
`;

const Social = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 2rem;

  a {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 44px;
    height: 44px;
    border: 1px solid #dce5df;
    border-radius: 50%;
    color: #47594f;
    font-size: 1.1rem;
    transition:
      background-color 180ms ease,
      color 180ms ease;

    &:hover {
      background: #e4f5ec;
      color: #007b61;
    }
  }

  > span {
    flex-basis: 100%;
    margin-top: 0.5rem;
    color: #65736b;
    font-size: 0.7rem;
    line-height: 1.6;
  }

  @media (min-width: 1100px) {
    > span {
      flex-basis: auto;
      margin: 0 0 0 0.75rem;
    }
  }

  @media (max-width: 839px) {
    justify-content: center;
    gap: 0.6rem;
    margin-top: 1.25rem;

    > span {
      margin-top: 0.15rem;
      font-size: 0.65rem;
    }
  }
`;

const Portrait = styled.figure`
  width: min(100%, 14.4rem);
  margin: 0 auto;
  animation: ${enter} 750ms 100ms ease both;

  figcaption {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-top: 1.75rem;
    font-size: 0.75rem;
    line-height: 1.8;
    color: #65736b;

    strong {
      font-weight: 500;
      color: #354b42;
    }
  }

  .caption-line {
    width: 2rem;
    height: 1px;
    background: #91b4a3;
  }

  @media (min-width: 661px) {
    width: min(100%, 16rem);
  }
  @media (min-width: 840px) {
    width: min(100%, 20rem);
  }

  @media (max-width: 839px) {
    width: clamp(10.5rem, 42vw, 14rem);

    figcaption {
      justify-content: center;
      margin-top: 1rem;
      font-size: 0.7rem;
      line-height: 1.65;
    }

    .caption-line {
      display: none;
    }
  }
`;

const PhotoFrame = styled.div`
  position: relative;
  padding: 0.55rem;
  border: 1px solid #d6e8df;
  border-radius: 9rem 9rem 2rem 2rem;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 24px 60px -24px rgba(36, 74, 53, 0.25);

  img {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 3 / 4;
    object-fit: cover;
    border-radius: 8.5rem 8.5rem 1.5rem 1.5rem;
  }
`;

const CodeBadge = styled.span`
  position: absolute;
  bottom: 1.5rem;
  right: -0.7rem;
  display: grid;
  place-items: center;
  width: 3.5rem;
  height: 3.5rem;
  border: 5px solid #f3faf5;
  border-radius: 1.1rem;
  background: #183c32;
  color: #c2f4d9;
  font-size: 1.5rem;
  transform: rotate(8deg);

  @media (max-width: 839px) {
    width: 2.75rem;
    height: 2.75rem;
    right: -0.5rem;
    bottom: 1rem;
    border-width: 4px;
    border-radius: 0.9rem;
    font-size: 1.2rem;
  }
`;

const Footnote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 2rem;
  margin-top: 4rem;
  padding-top: 1.25rem;
  border-top: 1px solid #dce5df;
  color: #65736b;
  font-size: 0.7rem;
  line-height: 1.7;

  @media (max-width: 839px) {
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 2rem;
    padding-top: 1rem;
    text-align: center;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 44px;
    color: #354b42;
    text-decoration: none;

    &:hover {
      color: #007b61;
    }
  }
`;
